import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Zap, ArrowLeft, CheckCircle2 } from "lucide-react";
import { getFounderId } from "@/lib/founder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Upgrade() {
  const nav = useNavigate();
  const [usage, setUsage] = useState(null);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/usage/${getFounderId()}`);
      setUsage(data);
    } catch (error) {
      console.warn("Upgrade: failed to fetch usage", error);
    }
  }, []);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const capped = usage?.over_limit;

  return (
    <div className="min-h-screen grain-bg" data-testid="upgrade-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-zinc-200/60">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="upgrade-home-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-bold tracking-tight">velo2one</span>
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 py-14">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-[#FFF4E5] text-[#B27300] px-3 py-1 text-xs font-semibold mb-6"
          data-testid="upgrade-badge"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {capped ? "You've hit your validation quota" : "Upgrade preview"}
        </div>

        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.02] text-zinc-900 mb-4">
          {capped ? "You've used every report on this browser." : "More validations, more angles, more clarity."}
        </h1>
        <p className="text-zinc-600 text-lg max-w-2xl leading-relaxed">
          {capped
            ? "velo2one is free during our founder beta, but each browser gets a fixed number of validations while we tune the product."
            : "This is a preview of the upgrade flow velo2one will roll out after beta. During beta every founder gets a generous free allotment."}
        </p>

        {usage && (
          <div className="velo-card p-6 mt-8" data-testid="upgrade-usage-card">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Your usage on this browser</p>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-4xl text-zinc-900">{usage.credits_used}</span>
              <span className="text-zinc-500">/ {usage.credits_limit} reports</span>
            </div>
            <div className="w-full h-2 bg-zinc-100 rounded-full mt-3 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, (usage.credits_used / usage.credits_limit) * 100)}%`,
                  background: usage.over_limit ? "#FF4757" : "#FFA502",
                }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <PlanCard
            name="Founder Beta"
            price="Free"
            tagline="What you're on now"
            features={[
              "50 validation reports",
              "Full go-to-market analysis",
              "Live web-grounded signals",
              "Shareable report links",
            ]}
            highlight={false}
            cta="Current plan"
            disabled
          />
          <PlanCard
            name="Founder Pro"
            price="Coming soon"
            tagline="For founders in active discovery"
            features={[
              "Unlimited validation reports",
              "Deeper competitor scans",
              "PDF export + investor share view",
              "Priority model latency",
            ]}
            highlight
            cta="Notify me"
            disabled
          />
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            onClick={() => nav("/analyze")}
            className="rounded-full px-6 py-3 text-sm font-semibold border border-zinc-300 hover:border-zinc-900 transition-colors"
            data-testid="try-anyway-btn"
            disabled={capped}
          >
            {capped ? "Quota reached" : "Run another analysis"}
          </button>
          <Link
            to="/"
            className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
            data-testid="upgrade-back-home"
          >
            Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}

function PlanCard({ name, price, tagline, features, highlight, cta, disabled }) {
  return (
    <div
      className={`velo-card p-8 ${highlight ? "ring-2 ring-[#FF4757] relative" : ""}`}
      data-testid={`plan-card-${name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {highlight && (
        <span className="absolute -top-3 left-6 rounded-full bg-[#FF4757] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
          Preview
        </span>
      )}
      <p className="text-xs uppercase tracking-widest text-zinc-500">{tagline}</p>
      <h3 className="font-bold text-2xl mt-2">{name}</h3>
      <p className="font-serif-display text-4xl mt-3 text-zinc-900">{price}</p>
      <ul className="mt-6 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
            <CheckCircle2 className="w-4 h-4 text-[#2ED573] mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      <button
        disabled={disabled}
        className={`mt-6 w-full rounded-full py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 ${
          highlight ? "btn-primary text-white" : "border border-zinc-300 text-zinc-500"
        } ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {highlight && <Zap className="w-4 h-4" />}
        {cta}
      </button>
    </div>
  );
}
